import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { stylesToResponsiveCssPropsByKeys } from '@grebban/style-system-react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import useBreakpoint from '@fogia/core-web/hooks/useBreakpoint';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import { Search } from '@fogia/core-web/libs/Algolia-v2';
import objectKeysToCamelCase from '@grebban/utils/object/keysToCamelCase';
import Button from 'components/Button';
import SearchInputField from 'components/SearchInputField';
import Text from 'components/Text';
import applicationBrandingTypography from 'config/branding/typography';
import { ChevronRight } from 'assets/media/icons/ChevronRight';
import { isWideProduct } from 'Utils/isWideProduct';
import SearchResultProductRow from 'components/Header/SearchResultProductRow';
import { above, media } from '@fogia/core-web/utils/mediaqueries';

/* --------------- Typography constants --------------- */

const TITLE_TYPOGRAPHY: (string | null)[] = [
    'Primary/Mobile/18_120_510_-3',
    null,
    null,
    null,
    null,
    'Primary/20_120_510_-3',
];
const INPUT_TYPOGRAPHY: (string | null)[] = ['Primary/24_110_510_-3', null, null, null, null, 'Primary/30_100_510_-5'];
const SECTION_LABEL_TYPOGRAPHY: (string | null)[] = [
    'Secondary/Mobile/13_120_400',
    null,
    null,
    null,
    null,
    'Secondary/15_120_400',
];
const LINK_TYPOGRAPHY: (string | null)[] = [
    'Primary/Mobile/18_120_510_-3',
    null,
    null,
    null,
    null,
    'Primary/20_120_510_-3',
];
const BUTTON_TYPOGRAPHY: (string | null)[] = [
    'Primary/Mobile/13_100_510_-1',
    null,
    null,
    null,
    null,
    'Primary/15_120_510_-1',
];

/* --------------- Overlay wrapper (fade in/out, covers entire viewport) --------------- */

const OverlayWrapper = styled('div')<{ $isOpen: boolean }>`
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    z-index: 101;
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    transition:
        opacity var(--transition-primary-fast),
        visibility var(--transition-primary-fast);
`;

const OverlayBackdrop = styled('div')`
    position: absolute;
    inset: 0;
    background-color: var(--overlay-overlay-background);
    z-index: 0;
`;

/* --------------- Panel --------------- */

const Panel = styled('div')<{ $isMobile: boolean }>`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    max-width: 100vw;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: var(--ui-background);
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.08);
    width: 100%;

    ${above['tablet.lg']} {
        width: var(--overlay-width-desktop);
    }
`;

/* --------------- Panel header (orange bar) --------------- */

const PanelHeader = styled('div')`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--grid-gap);
    background-color: var(--ui-secondary);
    flex-shrink: 0;
`;

const CloseButton = styled(Button)`
    text-decoration: underline;
`;

/* --------------- Content area --------------- */

const Content = styled('div')`
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 40px;
    padding: 20px var(--grid-gap);
`;

/* --------------- Sections --------------- */

const SearchLinkList = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SearchLinkItem = styled('button')`
    text-decoration: underline;
    text-align: left;
    color: var(--ui-primary);
    background: none;
    border: none;
    padding: 0;

    ${media.hover} {
        &:hover {
            opacity: 0.7;
        }
    }
`;

/* --------------- Bottom bar --------------- */

const BottomBar = styled('div')`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: var(--grid-gap);
    background: linear-gradient(to top, var(--ui-background) 70%, transparent);
    z-index: 2;
`;

const SeeAllButton = styled(Button)`
    width: 100%;
    padding: var(--grid-gap);
    gap: var(--grid-gap);
    align-items: center;

    > span {
        display: flex;
        align-items: center;
        gap: var(--grid-gap);

        > span {
            flex: 1;
            text-align: left;
        }
    }
`;

const DEBOUNCE_MS = 300;
const SEARCH_LENGTH = 5;

/* --------------- Component --------------- */

export interface SearchOverlayProps {
    isOpen: boolean;
    onClose: () => void;
}

export const SearchOverlay = ({ isOpen, onClose }: SearchOverlayProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const isMobile = useBreakpoint({ to: 'desktop.sm' });
    const panelRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Local state
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [totalHits, setTotalHits] = useState(0);

    // Redux selectors
    const searchInHeader = useAppSelector(({ application }) => application?.config?.searchInHeader);
    const searchSuggestions =
        useAppSelector(({ application }) => application?.config?.header?.search?.searchSuggestions) ||
        searchInHeader?.searchSuggestions ||
        [];
    const popularProductsRaw = searchInHeader?.popularProducts || [];
    const applicationReferences = useAppSelector(({ application }) => application?.references) ?? {};
    const searchPageUrl =
        useAppSelector(({ application }) => application?.config?.options?.specifiedPages?.specifiedPages?.search) ||
        '/search';
    const locale = useAppSelector(({ application }) => application?.site?.locale) || '';

    // Resolve popular products from references
    const popularProducts = popularProductsRaw
        .map((item: any) => {
            const productId = item?.productId;
            if (!productId) return null;
            const ref = applicationReferences[productId];
            if (!ref) return null;
            return { ...ref, productId };
        })
        .filter(Boolean);

    // Navigation helpers
    const navigateToSearch = useCallback(
        (term: string) => {
            if (searchPageUrl && term) {
                history.push(`${searchPageUrl}?search=${encodeURI(term)}`);
                onClose();
            }
        },
        [searchPageUrl, history, onClose],
    );

    const navigateToProduct = useCallback(
        (product: any) => {
            const url = product.slug || product.url;
            if (url) {
                history.push(url);
                onClose();
            }
        },
        [history, onClose],
    );

    // Debounced Algolia search
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        const trimmedQuery = query.trim();

        if (!trimmedQuery) {
            setResults([]);
            setTotalHits(0);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);

        debounceRef.current = setTimeout(async () => {
            try {
                const indexName = `products_${locale.toLowerCase()}`;
                const response = await Search(indexName, trimmedQuery, {
                    offset: 0,
                    length: SEARCH_LENGTH,
                    distinct: true,
                    facets: ['*'],
                });

                const hits = response?.results?.[0]?.hits || [];
                const nbHits = response?.results?.[0]?.nbHits || 0;

                const camelizedHits = hits.map((hit: any) =>
                    objectKeysToCamelCase({ ...hit }, { recursive: true, modifyInputObject: false }),
                );

                setResults(camelizedHits);
                setTotalHits(nbHits);
            } catch (error) {
                console.error('Search error:', error);
                setResults([]);
                setTotalHits(0);
            } finally {
                setIsSearching(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [query, locale]);

    // Reset state when overlay closes
    useEffect(() => {
        if (!isOpen) {
            setQuery('');
            setResults([]);
            setIsSearching(false);
            setTotalHits(0);
        }
    }, [isOpen]);

    // Focus input when opening
    useEffect(() => {
        if (isOpen) {
            const timeout = setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timeout);
        }
    }, [isOpen]);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Close on route change
    const { pathname } = useLocation();
    const prevPathnameRef = useRef(pathname);
    useEffect(() => {
        if (prevPathnameRef.current !== pathname) {
            prevPathnameRef.current = pathname;
            onClose();
        }
    }, [pathname, onClose]);

    const hasQuery = query.trim().length > 0;
    const hasResults = results.length > 0;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (hasQuery) {
            navigateToSearch(query.trim());
        }
    };

    const getProductThumbnail = (product: any): string | null => product.thumbnail || product.imagery?.[0] || null;
    return (
        <OverlayWrapper $isOpen={isOpen} aria-hidden={!isOpen} data-theme="light">
            <OverlayBackdrop
                onClick={onClose}
                onKeyDown={(e) => e.key === 'Escape' && onClose()}
                role="button"
                tabIndex={-1}
                aria-label={t('header.close_menu')}
            />
            <Panel
                ref={panelRef}
                $isMobile={isMobile}
                role="dialog"
                aria-modal="true"
                aria-label={t('header.search_label', 'Search')}
            >
                <PanelHeader>
                    <Text $as="span" typography={TITLE_TYPOGRAPHY} color="#171717">
                        {t('header.search_label', 'Search')}
                    </Text>
                    <CloseButton
                        theme="textButton"
                        typography={TITLE_TYPOGRAPHY}
                        type="button"
                        onClick={onClose}
                        color="#171717"
                    >
                        {t('header.close', 'Close')}
                    </CloseButton>
                </PanelHeader>
                <Content>
                    <form onSubmit={handleSubmit}>
                        <SearchInputField
                            ref={inputRef}
                            type="text"
                            placeholder={t('header.search.placeholder')}
                            aria-label={t('header.search.placeholder')}
                            value={query}
                            onChange={setQuery}
                            typography={INPUT_TYPOGRAPHY}
                            action={
                                hasQuery
                                    ? {
                                          label: t('header.clear_search', 'Clear search'),
                                          onClick: () => setQuery(''),
                                          typography: BUTTON_TYPOGRAPHY,
                                      }
                                    : undefined
                            }
                        />
                    </form>

                    {/* Popular searches (shown when no query) */}
                    {!hasQuery && searchSuggestions.length > 0 && (
                        <div>
                            <Text
                                $as="span"
                                typography={SECTION_LABEL_TYPOGRAPHY}
                                color="var(--ui-primary)"
                                marginBottom="16px"
                                display="block"
                            >
                                {t('header.popular_searches', 'Popular searches')}
                            </Text>
                            <SearchLinkList>
                                {searchSuggestions
                                    .map((suggestion: string | { label: string; searchTerm: string }) => {
                                        const label = typeof suggestion === 'string' ? suggestion : suggestion.label;
                                        const searchTerm =
                                            typeof suggestion === 'string'
                                                ? suggestion
                                                : suggestion.searchTerm || suggestion.label;

                                        if (!searchTerm) return null;

                                        return (
                                            <SearchLinkItem
                                                key={searchTerm}
                                                type="button"
                                                onClick={() => navigateToSearch(searchTerm)}
                                                {...(stylesToResponsiveCssPropsByKeys(
                                                    LINK_TYPOGRAPHY as string[],
                                                    applicationBrandingTypography,
                                                ) as Record<string, unknown>)}
                                            >
                                                {label || searchTerm}
                                            </SearchLinkItem>
                                        );
                                    })
                                    .filter(Boolean)}
                            </SearchLinkList>
                        </div>
                    )}

                    {/* Popular products (shown when no query) */}
                    {!hasQuery && popularProducts.length > 0 && (
                        <div>
                            <Text
                                $as="span"
                                typography={SECTION_LABEL_TYPOGRAPHY}
                                color="var(--ui-primary)"
                                marginBottom="16px"
                                display="block"
                            >
                                {t('header.popular_products', 'Popular products')}
                            </Text>
                            {popularProducts.map((productRef: any) => {
                                const product = productRef.product || productRef;
                                const designerName =
                                    typeof product.designer === 'string'
                                        ? product.designer
                                        : product.designer?.name || null;

                                return (
                                    <SearchResultProductRow
                                        key={productRef.externalId || productRef.slug}
                                        name={product.name}
                                        designer={designerName}
                                        thumbnailUrl={product.thumbnail || product.imagery?.[0] || null}
                                        isWide={isWideProduct(product)}
                                        onClick={() => onClose()}
                                        url={productRef.slug}
                                    />
                                );
                            })}
                        </div>
                    )}

                    {/* Search results (shown when query has results) */}
                    {hasQuery && hasResults && (
                        <div>
                            <Text
                                $as="span"
                                typography={SECTION_LABEL_TYPOGRAPHY}
                                color="var(--ui-primary)"
                                marginBottom="16px"
                                display="block"
                            >
                                {t('header.products', 'Products')}
                            </Text>
                            {results.map((product: any) => (
                                <SearchResultProductRow
                                    key={product.objectId || product.objectID || product.name}
                                    name={product.name}
                                    designer={product.designer}
                                    thumbnailUrl={getProductThumbnail(product)}
                                    isWide={isWideProduct(product)}
                                    url={product.slug}
                                    onClick={() => onClose()}
                                />
                            ))}
                        </div>
                    )}

                    {/* No results message */}
                    {hasQuery && !hasResults && !isSearching && (
                        <Text $as="p" typography={SECTION_LABEL_TYPOGRAPHY} color="var(--ui-primary)">
                            {t('header.no_results', 'No results found')}
                        </Text>
                    )}
                </Content>

                {/* See all results button */}
                {hasQuery && (
                    <BottomBar data-theme="light">
                        <SeeAllButton
                            theme="filledSecondary"
                            type="button"
                            onClick={() => navigateToSearch(query.trim())}
                            {...(stylesToResponsiveCssPropsByKeys(
                                BUTTON_TYPOGRAPHY as string[],
                                applicationBrandingTypography,
                            ) as Record<string, unknown>)}
                        >
                            <span>
                                {totalHits > 0
                                    ? `${t('header.see_all_results', 'See all results')} (${totalHits})`
                                    : t('header.see_all_results', 'See all results')}
                            </span>
                            <ChevronRight size="xsmall" />
                        </SeeAllButton>
                    </BottomBar>
                )}
            </Panel>
        </OverlayWrapper>
    );
};
